import { useEffect, useState } from 'react'
import { api } from '../lib/api'

export interface ReviewShare {
  token: string; path: string; root: string; url: string; review: boolean; created_at: string
  stats?: { comments: number; unresolved: number; approvals: number; last_comment_at: string | null }
}

export const reviewStatsLabel = (s: ReviewShare | null | undefined) => {
  const st = s?.stats; if (!st) return 'aucun commentaire'
  return `${st.comments} commentaire${st.comments > 1 ? 's' : ''}${st.unresolved ? ` (${st.unresolved} à traiter)` : ''} · ${st.approvals} approuvé${st.approvals > 1 ? 's' : ''}`
}

export async function copyText(text: string) {
  try { await navigator.clipboard.writeText(text); return true } catch { prompt('Copiez le lien :', text); return false }
}

/** Charge (ou crée) le lien de revue du dossier livrables d'un client. */
export async function findReviewShare(slug: string) {
  const j = await api<{ shares: ReviewShare[] }>('/stock/shares')
  return j.shares.find(x => x.root === 'clients' && x.path === slug && x.review) || null
}
export async function createReviewShare(slug: string) {
  const r = await api<{ share: ReviewShare }>('/stock/share', { method: 'POST', json: { root: 'clients', path: slug, review: true } })
  return r.share
}

const btn = 'px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-widest transition-colors'

/**
 * Bloc « Lien de revue » pour le dossier livrables d'un client : le client voit toutes les vidéos et photos,
 * commente (moment précis / épingle) et approuve, sans téléchargement.
 */
export default function ReviewLink({ slug, disabled, onMsg, compact }: { slug: string; disabled?: boolean; onMsg?: (m: string) => void; compact?: boolean }) {
  const [share, setShare] = useState<ReviewShare | null>(null)
  const [busy, setBusy] = useState(false)
  const say = (m: string) => onMsg?.(m)

  useEffect(() => { if (slug) findReviewShare(slug).then(setShare).catch(() => setShare(null)); else setShare(null) }, [slug])

  async function create() {
    setBusy(true)
    try {
      const s = await createReviewShare(slug); setShare(s)
      const ok = await copyText(s.url)
      say(`${ok ? '🔗 Lien de revue copié : ' + s.url + ' · ' : ''}Envoyez-le au client : il verra toutes les vidéos et photos livrées, pourra commenter et approuver, sans téléchargement.`)
    } catch (e: any) { say('Erreur : ' + e.message) } finally { setBusy(false) }
  }
  async function revoke() {
    if (!share || !confirm('Révoquer le lien de revue ? Le client n’y aura plus accès et les commentaires seront effacés.')) return
    try { await api(`/stock/share?token=${share.token}`, { method: 'DELETE' }); setShare(null); say('Lien de revue révoqué.') } catch (e: any) { say('Erreur : ' + e.message) }
  }

  if (!slug) return null
  if (!share) return <button onClick={create} disabled={disabled || busy} title="Créer un lien public de revue : vidéos et photos, commentaires et approbation, sans téléchargement" className={`${btn} bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-40`}>💬 Lien de revue</button>

  return (
    <div className={`flex flex-wrap items-center gap-3 text-xs ${compact ? '' : 'border border-sky-300/30 rounded px-3 py-2 bg-sky-300/5'}`}>
      <span className="text-sky-300 font-bold uppercase tracking-widest">💬 Lien de revue actif</span>
      <span className={share.stats?.unresolved ? 'text-orange' : 'text-acier'}>{reviewStatsLabel(share)}</span>
      <button onClick={async () => { if (await copyText(share.url)) say('🔗 Lien de revue copié : ' + share.url) }} className="text-orange uppercase tracking-widest hover:underline">Copier</button>
      <a href={share.url} target="_blank" rel="noreferrer" className="text-acier uppercase tracking-widest hover:text-orange">Ouvrir</a>
      <button onClick={revoke} className="text-acier uppercase tracking-widest hover:text-red-300">Révoquer</button>
    </div>
  )
}
